import {Injectable} from '@angular/core';
import {RequesterService} from "../../shared/services/requester/requester.service";
import {DbResponse} from "../../shared/interceptors/db-response";
import {Product, ProductType} from "./product";
import {ProductMapper} from "./product-mapper";
import {BehaviorSubject, combineLatest, map} from "rxjs";
import {Config} from "../../config/config";
import {SortProductsByFreeInstalledPipe} from './sort-products-by-free-installed.pipe';
import {User} from "../../user/user";
import * as fromStore from '../../store';
import {DbStoreState} from '../../store';
import {Store} from '@ngrx/store';
import mockData from "../../../assets/json/mock-data.json";

@Injectable()
export class ProductService {
  public _user: User;
  private ownerWorkspaceId: any;
  private currentWorkspaceId: any;

  constructor(
    private _requester: RequesterService,
    private _sortProductsByFreeInstalledPipe: SortProductsByFreeInstalledPipe,
    private _store: Store<DbStoreState>
  ) {
    this._store.select(fromStore.getWorkspaceIdEntities).subscribe(workspaceIdEntities => {
      this.ownerWorkspaceId = workspaceIdEntities.ownerWorkspaceId;
      this.currentWorkspaceId = workspaceIdEntities.currentWorkspaceId || this.ownerWorkspaceId;
    });
  }

  public getTenWebProducts(demoUser): BehaviorSubject<DbResponse> {
    if (demoUser) {
      const response: any = mockData['all-products'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return this._requester.core.get(`workspaces/${this.currentWorkspaceId}/products`);
  }


  public getWebsitePlugins(websiteId: number): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['plugins'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return this._requester.manager.get(`workspaces/${this.currentWorkspaceId}/manager/domains/${websiteId}/plugins`, null, true);
  }

  public getWebsiteExtensions(websiteId: number): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['addons'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return this._requester.manager.get(`workspaces/${this.currentWorkspaceId}/manager/domains/${websiteId}/addons`, null, true);
  }

  public getWebsiteThemes(websiteId: number): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['themes'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return this._requester.manager.get(`workspaces/${this.currentWorkspaceId}/manager/domains/${websiteId}/themes`, null, true);
  }

  public updateScreenShot(websiteId: number): BehaviorSubject<DbResponse> {
    return this._requester.core
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/update-screenshot`, null, null);
  }

  public getPlugins(websiteId: number): BehaviorSubject<DbResponse> {
    return (<BehaviorSubject<DbResponse>>(combineLatest(
      this._requester.manager.get(`workspaces/${this.currentWorkspaceId}/products/plugins`, null, false),
      this._requester.manager.get(`workspaces/${this.currentWorkspaceId}/manager/domains/${websiteId}/plugins`, null, true)
    ).pipe(map((responseList: DbResponse[]): DbResponse => {
      const hasNullResponse: boolean = !!responseList.filter((response: DbResponse): boolean => !response).length;
      if (hasNullResponse) {
        return null;
      }

      const failedResponse: DbResponse = responseList.find((response: DbResponse): boolean => !response.success);
      if (failedResponse) {
        return failedResponse;
      }
      let products: Product[] = ProductMapper.mapProducts(responseList[0].data, responseList[1].data.products, responseList[1].data.is_network === 1, ProductType.PLUGIN, this._user);
      // remove manager from products
      products = products.filter((product: Product): boolean => product.slug !== Config.managerPluginSlug);
      products = this._sortProductsByFreeInstalledPipe.transform(products);

      const response: DbResponse = new DbResponse();
      response.success = true;
      response.data = products;

      return response;
    }))));
  }

  public getWordpressPlugins(websiteId: number, searchTerm: string, featured: boolean = false): BehaviorSubject<DbResponse> {
    const demoUser =websiteId == Config.demoDomainId
    const params: any = {
      action: 'query_plugins',
      timeout: 15,
      request: {
        search: searchTerm || '',
        browse: !searchTerm ? 'popular' : '',
        per_page: 15,
        page: 1,
        fields: {
          sections: false,
          icon: true,
          short_description: true,
          homepage: true,
          ratings: true,
          requires: true,
          downloaded: true,
          tags: false,
          donate_link: false,
          active_installs: true,
          reviews: true,
          icons: true
        }
      }
    };

    if (demoUser) {
      const response: DbResponse = new DbResponse();
      response.success = true;
      response.data = mockData['plugins'].data.products;
      return new BehaviorSubject<DbResponse>(response);
    }

    return (<BehaviorSubject<DbResponse>>(combineLatest(
      this._requester.wordpress.jsonp('plugins/info/1.1/', params, false),
      this._requester.manager.get(`workspaces/${this.currentWorkspaceId}/manager/domains/${websiteId}/plugins`, null, true)
    ).pipe(map((responseList: DbResponse[]): DbResponse => {
      const hasNullResponse: boolean = !!responseList.filter((response: DbResponse): boolean => !response).length;
      if (hasNullResponse) {
        return null;
      }

      const failedResponse: DbResponse = responseList.find((response: DbResponse): boolean => !response.success);
      if (failedResponse) {
        return failedResponse;
      }
      if (featured) {
        responseList[0].data.plugins = responseList[0].data.plugins.filter((plugin) => plugin.slug === searchTerm);
      }
      let products: Product[] = ProductMapper.mapWordpressPlugins(responseList[0].data.plugins, responseList[1].data.products, responseList[1].data.is_network === 1);

      const response: DbResponse = new DbResponse();
      response.success = true;
      response.data = products;

      return response;
    }))));
  }

  public getWordpressVersion(websiteId): BehaviorSubject<DbResponse> {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['wordpress-version'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.wordpress.get(`core/version-check/1.7/`, null, false)
      .pipe(map((response: DbResponse): DbResponse => {
        return response;
      })));
  }

  public getWordpressThemes(websiteId: number, searchTerm: string): BehaviorSubject<DbResponse> {
    const params: any = {
      action: 'query_themes',
      timeout: 15,
      request: {
        search: searchTerm || '',
        browse: !searchTerm ? 'popular' : '',
        per_page: 15,
        page: 1,
        fields: {
          description: true,
          sections: false,
          rating: true,
          ratings: true,
          downloaded: true,
          download_link: true,
          last_updated: true,
          homepage: true,
          tags: true,
          template: true,
          screenshot_url: true,
          active_installs: false,
          requires: true
        }
      }
    };
    return (<BehaviorSubject<DbResponse>>(combineLatest(
      this._requester.wordpress.jsonp('themes/info/1.1/', params, false),
      this._requester.manager.get(`workspaces/${this.currentWorkspaceId}/manager/domains/${websiteId}/themes`, null, false)
    ).pipe(map((responseList: DbResponse[]): DbResponse => {
      const hasNullResponse: boolean = !!responseList.filter((response: DbResponse): boolean => !response).length;
      if (hasNullResponse) {
        return null;
      }
      const failedResponse: DbResponse = responseList.find((response: DbResponse): boolean => !response.success);
      if (failedResponse) {
        return failedResponse;
      }
      let products: Product[] = ProductMapper.mapWordpressThemes(responseList[0].data.themes, responseList[1].data.products, responseList[1].data.is_network === 1);

      const response: DbResponse = new DbResponse();
      response.success = true;
      response.data = products;

      return response;
    }))));
  }

  public getBackupLimit(): BehaviorSubject<DbResponse> {
    return this._requester.backup.get(`workspaces/${this.currentWorkspaceId}/tenweb-cloud/limits`);
  }

  public getChangelog(productId: number): BehaviorSubject<DbResponse> {
    return this._requester.core.get(`workspaces/${this.currentWorkspaceId}/products/${productId}/versions`);
  }

  public startDownloadingPlugin(productId: number) {
    return <BehaviorSubject<DbResponse>>(this._requester.core
      .get(`workspaces/${this.currentWorkspaceId}/products/${productId}/download`, null, false, true)
      .pipe(map((response: DbResponse) => {
        return response;
      })));
  }

  public getSchedulePlugins(websiteId: number) {
    if (+websiteId === Config.demoDomainId) {
      const response: DbResponse = mockData['plugin'] as any;
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.manager
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/products-update/plugin`, null, true)
      .pipe(map((response: DbResponse) => {
        return response;
      })));
  }

  public setSchedulePlugins(websiteId: number, data) {
    return this._requester.manager
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/products-update/create`, null, data, true);
  }

  public getScheduleThemes(websiteId: number) {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['theme'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.manager
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/products-update/theme`, null, true)
      .pipe(map((response: DbResponse) => {
        return response;
      })));
  }


  public setScheduleThemes(websiteId: number, data) {
    return this._requester.manager
      .post(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/products-update/create`, null, data, true);
  }

  public getPluginUpdateHistory(websiteId: number) {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['history'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.manager
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/products-update/plugin/history`, null, true)
      .pipe(map((response: DbResponse) => {
        if (response && response.success) {
          response.data = response.data.map(ProductMapper.mapProductHistory);
        }

        return response;
      })));
  }

  public getThemeUpdateHistory(websiteId: number) {
    if (+websiteId === Config.demoDomainId) {
      const response: any = mockData['history'];
      return new BehaviorSubject<DbResponse>(response);
    }
    return <BehaviorSubject<DbResponse>>(this._requester.manager
      .get(`workspaces/${this.currentWorkspaceId}/domains/${websiteId}/products-update/theme/history`, null, true)
      .pipe(map((response: DbResponse) => {
        if (response && response.success) {
          response.data = response.data.map(ProductMapper.mapProductHistory);
        }

        return response;
      })));
  }

}
